import { Injectable, inject } from '@angular/core';
import { AppConfigService } from '@app/core/config';
import { AuthService } from '@app/core/services/auth.service';
import { CurrentUserService } from '@app/core/services/current-user.service';
import { SpeechDto } from '@app/data/dto';
import * as signalR from '@microsoft/signalr';
import { Subject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class SpeechRealtimeService {
  private static readonly RealtimeSampleRate = 16000;
  private readonly appConfig = inject(AppConfigService);
  private readonly authService = inject(AuthService);
  private readonly currentUserService = inject(CurrentUserService);
  private speechConnection?: signalR.HubConnection;
  private transcribeConnection?: signalR.HubConnection;
  private readonly pendingAudioChunks: RealtimeAudioChunk[] = [];
  private audioQueueFlush?: Promise<void>;

  readonly speechUpdated$ = new Subject<SpeechDto>();
  readonly realtimeTranscript$ = new Subject<RealtimeTranscriptEvent>();

  async connectSpeech(): Promise<void> {
    if (this.speechConnection?.state === signalR.HubConnectionState.Connected) {
      return;
    }

    const currentUser = await this.currentUserService.getCurrentUser();
    if (!currentUser?.appUser?.appUserId) {
      return;
    }

    this.speechConnection = this.createConnection('speech');
    this.speechConnection.on('SpeechUpdated', (speech: SpeechDto) => this.speechUpdated$.next(speech));
    this.speechConnection.onreconnected(() => {
      void this.joinSpeechNotifications();
    });
    await this.speechConnection.start();
    await this.joinSpeechNotifications();
  }

  async connectTranscribe(): Promise<void> {
    if (this.transcribeConnection?.state === signalR.HubConnectionState.Connected) {
      return;
    }

    this.transcribeConnection = this.createConnection('transcribe');
    this.transcribeConnection.on('RealtimeTranscript', (event: RealtimeTranscriptEvent) => this.realtimeTranscript$.next(event));
    this.transcribeConnection.on('RealtimeTranscriptionCanceled', (event: unknown) => console.warn('Realtime transcription canceled', event));
    this.transcribeConnection.onreconnected(() => {
      void this.flushAudioQueue();
    });
    await this.transcribeConnection.start();
    await this.flushAudioQueue();
  }

  async startRealtimeTranscription(sessionId: string): Promise<void> {
    await this.connectTranscribe();
    if (this.transcribeConnection?.state !== signalR.HubConnectionState.Connected) {
      return;
    }

    await this.transcribeConnection.invoke('StartRealtimeTranscription', sessionId, SpeechRealtimeService.RealtimeSampleRate);
  }

  sendPcmAudioChunk(sessionId: string, audioChunk: Int16Array): void {
    if (audioChunk.length === 0) {
      return;
    }

    const bytes = new Uint8Array(audioChunk.buffer, audioChunk.byteOffset, audioChunk.byteLength);
    this.pendingAudioChunks.push({ sessionId, audioChunk: Array.from(bytes) });
    void this.flushAudioQueue();
  }

  async stopRealtimeTranscription(sessionId: string): Promise<void> {
    while (this.pendingAudioChunks.some((chunk) => chunk.sessionId === sessionId)) {
      await this.flushAudioQueue();
      if (this.pendingAudioChunks.some((chunk) => chunk.sessionId === sessionId)) {
        await this.waitForTranscribeConnection();
      }
    }

    await this.waitForTranscribeConnection();
    const connection = this.transcribeConnection;
    if (connection === undefined) {
      return;
    }

    await connection.invoke('StopRealtimeTranscription', sessionId);
  }

  private createConnection(endpoint: 'speech' | 'transcribe'): signalR.HubConnection {
    return new signalR.HubConnectionBuilder()
      .withUrl(`${this.appConfig.config().apiUrl}/v1/realtime/${endpoint}`, {
        accessTokenFactory: () => this.authService.getAuthToken() ?? '',
      })
      .withAutomaticReconnect()
      .build();
  }

  private async joinSpeechNotifications(): Promise<void> {
    if (this.speechConnection?.state !== signalR.HubConnectionState.Connected) {
      return;
    }

    await this.speechConnection.invoke('JoinSpeechNotifications');
  }

  private async flushAudioQueue(): Promise<void> {
    if (this.audioQueueFlush !== undefined) {
      await this.audioQueueFlush;
      return;
    }

    this.audioQueueFlush = this.flushAudioQueueCore();
    try {
      await this.audioQueueFlush;
    } finally {
      this.audioQueueFlush = undefined;
    }
  }

  private async flushAudioQueueCore(): Promise<void> {
    while (this.pendingAudioChunks.length > 0) {
      await this.connectTranscribe();
      if (this.transcribeConnection?.state !== signalR.HubConnectionState.Connected) {
        return;
      }

      const nextChunk = this.pendingAudioChunks[0];
      try {
        await this.transcribeConnection.invoke('SendAudioChunk', nextChunk.sessionId, nextChunk.audioChunk, SpeechRealtimeService.RealtimeSampleRate);
        this.pendingAudioChunks.shift();
      } catch {
        return;
      }
    }
  }

  private async waitForTranscribeConnection(): Promise<void> {
    while (this.transcribeConnection?.state !== signalR.HubConnectionState.Connected) {
      try {
        await this.connectTranscribe();
      } catch {
        await new Promise((resolve) => globalThis.setTimeout(resolve, 1000));
      }
    }
  }
}

interface RealtimeAudioChunk {
  readonly sessionId: string;
  readonly audioChunk: number[];
}

export interface RealtimeTranscriptEvent {
  readonly sessionId: string;
  readonly text: string;
  readonly isFinal: boolean;
  readonly offset: number;
  readonly duration: number;
  readonly speaker?: string | null;
}